import dayjs from 'dayjs';
import { Button, Checkbox, Flex, Input, Radio, Select, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { APP_TIMEZONE, formatDateTime } from '../../lib/format';
import {
  CASE_CATEGORIES,
  CASE_SEVERITIES,
  FOLLOW_UP_OUTCOMES,
  completeFollowUp,
  retryFollowUp,
  type CaseCategory,
  type CaseSeverity,
  type FollowUp,
  type FollowUpOutcome,
} from './api';
import { categoryLabel, severityLabel } from './labels';

const { Text } = Typography;

/** Outcomes where nobody spoke to the customer: no score is taken. */
const UNANSWERED: readonly FollowUpOutcome[] = ['NO_ANSWER', 'WRONG_NUMBER'];

type RetrySlot = 'IN_2H' | 'TOMORROW' | 'IN_3D';

/** The instant for a retry slot. Tomorrow 9:00 is read on the Kuala Lumpur clock. */
function slotTime(slot: RetrySlot): Date {
  if (slot === 'IN_2H') return dayjs().add(2, 'hour').toDate();
  if (slot === 'IN_3D') return dayjs().add(3, 'day').toDate();
  return dayjs().tz(APP_TIMEZONE).add(1, 'day').hour(9).minute(0).second(0).millisecond(0).toDate();
}

export interface FollowUpOutcomeDrawerProps {
  open: boolean;
  followUp: FollowUp;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Records how a call went. An issue can open a case in the same step, and no
 * answer can put the call back for later instead of closing it.
 */
export function FollowUpOutcomeDrawer({ open, followUp, onClose, onDone }: FollowUpOutcomeDrawerProps): ReactNode {
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState<FollowUpOutcome | undefined>(undefined);
  const [note, setNote] = useState('');
  const [score, setScore] = useState<number | undefined>(undefined);
  const [openCase, setOpenCase] = useState(true);
  const [category, setCategory] = useState<CaseCategory>('COMPLAINT');
  const [severity, setSeverity] = useState<CaseSeverity>('MEDIUM');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [slot, setSlot] = useState<RetrySlot | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const issue = outcome === 'REACHED_ISSUE';
  const withCase = issue && openCase;
  const asksScore = outcome !== undefined && !UNANSWERED.includes(outcome);
  const caseReady = subject.trim().length >= 3 && description.trim().length > 0;
  const retrying = outcome === 'NO_ANSWER' && slot !== undefined;
  const name = followUp.customer.name;

  const run = async (call: () => Promise<unknown>): Promise<void> => {
    setSubmitting(true);
    setFailure(null);
    try {
      await call();
      onDone();
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : t('serviceDesk.followUps.drawer.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = (): void => {
    if (outcome === undefined) return;
    void run(() =>
      completeFollowUp(followUp.uid, {
        outcome,
        ...(note.trim().length > 0 ? { note: note.trim() } : {}),
        ...(asksScore && score !== undefined ? { satisfactionScore: score } : {}),
        ...(withCase
          ? { openCase: { category, severity, subject: subject.trim(), description: description.trim() } }
          : {}),
      }),
    );
  };

  const slotLabel: Record<RetrySlot, string> = {
    IN_2H: t('serviceDesk.followUps.drawer.retryIn2h'),
    TOMORROW: t('serviceDesk.followUps.drawer.retryTomorrow'),
    IN_3D: t('serviceDesk.followUps.drawer.retryIn3d'),
  };

  const consequence =
    outcome === undefined
      ? t('serviceDesk.followUps.drawer.pick')
      : retrying
        ? t('serviceDesk.followUps.drawer.retryConsequence', {
            when: formatDateTime(slotTime(slot).toISOString()),
            count: followUp.attempts + 1,
          })
        : t(withCase ? 'serviceDesk.followUps.drawer.consequenceCase' : 'serviceDesk.followUps.drawer.consequence', { name });

  return (
    <ActionDrawer
      open={open}
      title={t('serviceDesk.followUps.drawer.title')}
      submitLabel={t('serviceDesk.followUps.drawer.submit')}
      submitting={submitting}
      // With a retry slot picked, Try again is the action; Record outcome would close the call instead.
      submitDisabled={outcome === undefined || retrying || (withCase && !caseReady)}
      consequence={consequence}
      onSubmit={submit}
      onClose={() => {
        setFailure(null);
        onClose();
      }}
      error={failure !== null ? <Text type="danger">{failure}</Text> : undefined}
    >
      <Flex vertical gap={16}>
        <Flex vertical gap={4}>
          <Text strong>{name}</Text>
          <Text type="secondary">{followUp.customer.phone}</Text>
        </Flex>

        <div>
          <Text strong id="fu-outcome-label">{t('serviceDesk.followUps.drawer.outcome')}</Text>
          <Radio.Group
            aria-labelledby="fu-outcome-label"
            value={outcome}
            onChange={(e) => {
              setOutcome(e.target.value as FollowUpOutcome);
              setSlot(undefined);
            }}
            style={{ display: 'flex', flexDirection: 'column', marginTop: 4 }}
            // A 44px row makes the whole line the tap target, not just the dot and its text.
            options={FOLLOW_UP_OUTCOMES.map((o) => ({
              value: o,
              label: t(`serviceDesk.followUpOutcome.${o}`),
              style: { minHeight: 44, alignItems: 'center' },
            }))}
          />
        </div>

        {outcome === 'NO_ANSWER' && (
          <Flex vertical gap={8}>
            <Text strong>{t('serviceDesk.followUps.drawer.retryTitle')}</Text>
            <Text type="secondary">{t('serviceDesk.followUps.drawer.retryHelp')}</Text>
            <Radio.Group
              value={slot}
              onChange={(e) => { setSlot(e.target.value as RetrySlot); }}
              optionType="button"
              buttonStyle="solid"
              style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
              options={(['IN_2H', 'TOMORROW', 'IN_3D'] as const).map((s) => ({ value: s, label: slotLabel[s] }))}
            />
            <Button
              size="large"
              disabled={slot === undefined}
              loading={submitting}
              onClick={() => {
                if (slot !== undefined) void run(() => retryFollowUp(followUp.uid, { dueAt: slotTime(slot).toISOString() }));
              }}
            >
              {t('serviceDesk.followUps.drawer.retrySubmit')}
            </Button>
          </Flex>
        )}

        {asksScore && (
          <div>
            <Text strong>{t('serviceDesk.followUps.drawer.score')}</Text>
            <div>
              <Text type="secondary">{t('serviceDesk.followUps.drawer.scoreHelp')}</Text>
            </div>
            <Radio.Group
              value={score}
              onChange={(e) => { setScore(e.target.value as number); }}
              optionType="button"
              style={{ marginTop: 8 }}
              options={[1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }))}
            />
          </div>
        )}

        {issue && (
          <Flex vertical gap={12}>
            <Checkbox checked={openCase} onChange={(e) => { setOpenCase(e.target.checked); }}>
              {t('serviceDesk.followUps.drawer.openCase')}
            </Checkbox>
            {openCase && (
              <Flex vertical gap={12}>
                <label>
                  <Text>{t('serviceDesk.followUps.drawer.caseCategory')}</Text>
                  <Select<CaseCategory>
                    size="large"
                    style={{ width: '100%' }}
                    value={category}
                    onChange={setCategory}
                    options={CASE_CATEGORIES.map((c) => ({ value: c, label: categoryLabel(t, c) }))}
                  />
                </label>
                <label>
                  <Text>{t('serviceDesk.followUps.drawer.caseSeverity')}</Text>
                  <Select<CaseSeverity>
                    size="large"
                    style={{ width: '100%' }}
                    value={severity}
                    onChange={setSeverity}
                    options={CASE_SEVERITIES.map((s) => ({ value: s, label: severityLabel(t, s) }))}
                  />
                </label>
                <label>
                  <Text>{t('serviceDesk.followUps.drawer.caseSubject')}</Text>
                  <Input size="large" maxLength={160} value={subject} onChange={(e) => { setSubject(e.target.value); }} />
                </label>
                <label>
                  <Text>{t('serviceDesk.followUps.drawer.caseDescription')}</Text>
                  <Input.TextArea
                    rows={3}
                    maxLength={5000}
                    value={description}
                    onChange={(e) => { setDescription(e.target.value); }}
                  />
                </label>
                {!caseReady && <Text type="secondary">{t('serviceDesk.followUps.drawer.caseRequired')}</Text>}
              </Flex>
            )}
          </Flex>
        )}

        <label>
          <Text>{t('serviceDesk.followUps.drawer.note')}</Text>
          <Input.TextArea rows={3} maxLength={2000} value={note} onChange={(e) => { setNote(e.target.value); }} />
        </label>
      </Flex>
    </ActionDrawer>
  );
}
